import { Loader2 } from "lucide-react";

import DashboardLayout from "@/components/layout/DashboardLayout";
import StripeConnectCard from "@/components/StripeConnectCard";
import { Input } from "@/components/ui/input";
import Typography from "@/components/ui/typography";
import CertificateSection from "@/features/founders/components/CertificateSection";
import DangerZoneSection from "@/features/founders/components/DangerZoneSection";
import ProfileDetailsSection from "@/features/founders/components/ProfileDetailsSection";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useStripeStatus } from "@/hooks/useStripeStatus";
import { FounderLevel } from "@/types/levels";

const FounderProfile = () => {
  const { user, isLoading } = useCurrentUser();
  const { stripeActive, stripeConnectUrl } = useStripeStatus();

  // Determine current level from user data
  const currentLevel: FounderLevel | null = (() => {
    const backendLevel = user?.level?.toLowerCase();
    if (!backendLevel) return null;
    if (backendLevel === "certified") return FounderLevel.STACK_CERTIFIED;
    if (backendLevel === "pro") return FounderLevel.STACK_PRO;
    if (backendLevel === "x") return FounderLevel.STACK_X;
    return null; // "starter" means no level unlocked
  })();

  // Show certificate section if user is certified level OR already has a certificate
  const showCertificateSection =
    currentLevel !== null || !!user?.certificateUrl;

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start">
          <div className="space-y-1">
            <Typography size="h2" className="text-[#1e1e24]">
              Settings
            </Typography>
            <Typography size="b2" className="text-[#767677]">
              Manage your profile information and settings.
            </Typography>
          </div>
        </div>
        <div>
          <Typography size="h3" className="text-[#1e1e24]">
            Profile
          </Typography>
        </div>

        <div className="grid gap-6">
          {/* Basic information */}
          <div className="bg-[#f9f9f9] rounded-2xl p-6 space-y-6">
            <Typography size="l2" className="font-semibold text-[#494949]">
              Basic information
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Input
                  id="name"
                  value={user ? `${user.firstName} ${user.lastName}` : ""}
                  disabled
                  placeholder="Tess Miller"
                  className="bg-[#fdfdfd]"
                />
              </div>
              <div className="space-y-1.5">
                <Input
                  id="email"
                  value={user?.email || ""}
                  disabled
                  placeholder="abc@gmail.com"
                  className="bg-[#fdfdfd]"
                />
              </div>
            </div>
          </div>

          {/* Payment setup - Stripe Connect */}
          <StripeConnectCard
            stripeActive={stripeActive}
            connectUrl={stripeConnectUrl}
          />

          {/* Niche and expertise */}
          <ProfileDetailsSection />

          {/* Certificate section - show if certified level or has certificate */}
          {showCertificateSection && <CertificateSection />}

          {/* Danger zone - subscription cancellation (paid levels only) */}
          <DangerZoneSection />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FounderProfile;
